import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  useScrollTrigger,
  styled,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import { useThemeMode } from '../../contexts/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageSelector from '../LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { AccountCircle } from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import { getCrmSession } from '../../crm/api/crmHttp';
import { PORTAL_NAV, isPortalNavActive } from './portalNav';
import useTenantPortalEntry from './useTenantPortalEntry';

// trigger/active yalnızca stil içindir; DOM'a öznitelik olarak düşmesin
const noStyleProps = { shouldForwardProp: (prop) => prop !== 'trigger' && prop !== 'active' };

const StyledAppBar = styled(AppBar, noStyleProps)(({ theme, trigger }) => ({
  background: trigger
    ? theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.96)'
      : 'rgba(17, 24, 39, 0.96)'
    : theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.72)'
      : 'rgba(17, 24, 39, 0.5)',
  backdropFilter: 'blur(10px)',
  boxShadow: trigger ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
  borderBottom: `1px solid ${trigger ? theme.palette.divider : 'transparent'}`,
  transition: 'all 0.3s ease-in-out',
}));

const Logo = styled(Typography, noStyleProps)(({ theme }) => ({
  fontWeight: 800,
  letterSpacing: -0.3,
  color: theme.palette.text.primary,
  transition: 'color 0.3s ease-in-out',
  fontSize: '1.4rem',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const NavButton = styled(Button, noStyleProps)(({ theme, active }) => ({
  color: theme.palette.text.primary,
  fontWeight: active ? 700 : 500,
  borderRadius: 10,
  minWidth: 0,
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.06),
  },
  ...(active && {
    color: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.08),
  }),
}));

const AuthButton = styled(Button)(() => ({
  borderRadius: '8px',
  padding: '8px 20px',
  transition: 'all 0.3s ease-in-out',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}));

const Navbar = ({ onDrawerToggle }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  // Kiracı paneli girişi: bağlı kira sözleşmesi varsa (menü açılınca tembel ve önbellekli kontrol)
  const showTenantPortal = useTenantPortalEntry(currentUser?.id, Boolean(anchorEl));
  // CRM'e giriş yapılmışsa CRM butonu kullanıcının adını gösterir ve doğrudan panele gider
  // (sayfa değişiminde tekrar okunur: CRM'den çıkış/giriş sonrası güncel kalır)
  const crmSession = useMemo(() => getCrmSession(), [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const crmInitials = crmSession
    ? `${crmSession.user.name?.[0] || ''}${crmSession.user.lastName?.[0] || ''}`.toLocaleUpperCase('tr') || 'C'
    : '';

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const go = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const accountMenuItems = [
    { label: t('navigation.myAdvertisements'), path: '/my-advertisements', icon: <ListAltIcon fontSize="small" /> },
    { label: t('savedSearch.menu'), path: '/kayitli-aramalar', icon: <SavedSearchIcon fontSize="small" /> },
    { label: t('navigation.chat'), path: '/chat', icon: <ChatIcon fontSize="small" /> },
    { label: t('notification.title'), path: '/notifications', icon: <NotificationsIcon fontSize="small" /> },
    ...(showTenantPortal ? [{ label: t('tenantPortal.menu'), path: '/kiraci', icon: <KeyOutlinedIcon fontSize="small" /> }] : []),
  ];

  const iconColor = { color: theme.palette.text.primary };
  const themeLabel = t(`theme.${mode === 'dark' ? 'light' : 'dark'}`);

  return (
    <StyledAppBar position="fixed" trigger={trigger ? 1 : 0}>
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: '70px', md: '80px' },
            height: { xs: '70px', md: '80px' },
          }}
        >
          <IconButton
            color="inherit"
            aria-label={t('sidebar.menu')}
            aria-controls="portal-drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{
              mr: 1.5,
              display: { xs: 'inline-flex', md: 'none' },
              color: theme.palette.text.primary,
            }}
          >
            <MenuIcon />
          </IconButton>

          <Logo component={Link} to="/" aria-label={t('portal.nav.homeAria')} sx={{ textDecoration: 'none', flexShrink: 0 }}>
            ODAZEN
          </Logo>

          <Box
            component="nav"
            aria-label={t('portal.nav.mainMenu')}
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              ml: { md: 2, lg: 4 },
              gap: { md: 0.25, lg: 0.5 },
              minWidth: 0,
            }}
          >
            {PORTAL_NAV.map((item) => {
              const active = isPortalNavActive(item.key, location);
              return (
                <NavButton
                  key={item.key}
                  component={Link}
                  to={item.to}
                  active={active ? 1 : 0}
                  aria-current={active ? 'page' : undefined}
                  sx={{ px: { md: 1, lg: 1.5 }, fontSize: { md: '0.875rem', lg: '0.9375rem' } }}
                >
                  {t(`portal.nav.${item.key}`)}
                  {item.key === 'agencies' ? (
                    <Box
                      component="span"
                      sx={{
                        display: { md: 'none', lg: 'inline-block' },
                        ml: 0.75,
                        px: 0.6,
                        py: 0.1,
                        borderRadius: 1,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        color: 'primary.main',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                      }}
                    >
                      CRM
                    </Box>
                  ) : null}
                </NavButton>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />

          {currentUser && (
            <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 0.25, mr: 0.5 }}>
              <IconButton component={Link} to="/my-advertisements" size="small" aria-label={t('navigation.myAdvertisements')} sx={iconColor}>
                <ListAltIcon />
              </IconButton>
              <IconButton component={Link} to="/chat" size="small" aria-label={t('navigation.chat')} sx={iconColor}>
                <ChatIcon />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
            <LanguageSelector />
            <Tooltip title={themeLabel}>
              <IconButton onClick={toggleTheme} size="small" aria-label={themeLabel} sx={iconColor}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            {crmSession ? (
              <Button
                component={Link}
                to="/crm"
                title={`${crmSession.fullName}${crmSession.agencyName ? ` · ${crmSession.agencyName}` : ''} — ${t('portal.nav.crmPanel')}`}
                aria-label={`${t('portal.nav.crmPanel')}: ${crmSession.fullName}`}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  pl: 0.5,
                  pr: { md: 0.5, lg: 1.5 },
                  py: 0.5,
                  borderRadius: '999px',
                  textTransform: 'none',
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                }}
              >
                <Avatar sx={{ width: 30, height: 30, fontSize: 12.5, fontWeight: 700, bgcolor: 'primary.main', color: '#fff' }}>
                  {crmInitials}
                </Avatar>
                <Box sx={{ textAlign: 'left', minWidth: 0, display: { md: 'none', lg: 'block' } }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, maxWidth: 140 }}>
                    {crmSession.fullName}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11.5, lineHeight: 1.25, color: theme.palette.text.secondary, maxWidth: 140 }}>
                    {t('portal.nav.crmPanel')}
                  </Typography>
                </Box>
              </Button>
            ) : null}
            {!currentUser ? (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                <AuthButton
                  component={Link}
                  to="/login"
                  variant="outlined"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    px: { md: 1.75, lg: 2.5 },
                  }}
                >
                  {t('navigation.login')}
                </AuthButton>
                <AuthButton component={Link} to="/register" variant="contained" color="primary" sx={{ px: { md: 1.75, lg: 2.5 } }}>
                  {t('navigation.register')}
                </AuthButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton
                  component={Link}
                  to="/chat"
                  aria-label={t('navigation.chat')}
                  sx={{ ...iconColor, display: { xs: 'none', md: 'inline-flex' } }}
                >
                  <ChatIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/notifications"
                  aria-label={t('notification.title')}
                  sx={{ ...iconColor, display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <NotificationsIcon />
                </IconButton>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  aria-label={t('portal.nav.account')}
                  aria-haspopup="menu"
                  aria-controls={anchorEl ? 'portal-account-menu' : undefined}
                  aria-expanded={anchorEl ? 'true' : undefined}
                  sx={iconColor}
                >
                  {currentUser.photoURL ? (
                    <Avatar src={currentUser.photoURL} alt={currentUser.displayName} sx={{ width: 34, height: 34 }} />
                  ) : (
                    <AccountCircle sx={{ width: 34, height: 34 }} />
                  )}
                </IconButton>
                <Menu
                  id="portal-account-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      boxShadow: theme.shadows[3],
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {accountMenuItems.map((item) => (
                    <MenuItem key={item.path} onClick={() => go(item.path)}>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      {item.label}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem onClick={() => go('/profile')}>
                    <ListItemIcon>
                      <PersonOutlineRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('profile.menu.profile')}
                  </MenuItem>
                  <MenuItem onClick={() => go('/profile/identity-verification')}>
                    <ListItemIcon>
                      <VerifiedUserOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('profile.menu.identityVerification')}
                  </MenuItem>
                  <MenuItem onClick={() => go('/profile/change-password')}>
                    <ListItemIcon>
                      <LockResetOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('profile.menu.changePassword')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    {t('profile.menu.logout')}
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar;
